// A4 通知 / push-token 總開關（spec 20 §8.1）。
// 「推播通知」總開關：關閉 = DELETE /v3/me/push-tokens/{token}；開啟 = 重新上報。
//
// Web v1 現況：本 web client 尚未實作瀏覽器 push 註冊（16 §2.2 L100「Web v1 無
// push」），故沒有自有 token 可上報。本卡片仍提供總開關 UI 並完整接好 DELETE 路徑：
// 若帳號上存在由其他 client（手機 App）登錄、而使用者選擇在此關閉的 token（存於
// localStorage `ls.account.push_token`），關閉即送 DELETE。無 token 時開關呈停用並說明。
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, deletePushToken } from '../../lib/api';

// 上報的 push token（若有；由 push 註冊流程寫入，web v1 通常為空）。
const PUSH_TOKEN_KEY = 'ls.account.push_token';
// 使用者在本機選擇的通知總開關偏好（預設開）。
const PUSH_PREF_KEY = 'ls.account.push_enabled';

function readToken(): string | null {
  try {
    return localStorage.getItem(PUSH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function readEnabled(): boolean {
  try {
    return localStorage.getItem(PUSH_PREF_KEY) !== '0';
  } catch {
    return true;
  }
}

export default function NotificationCard() {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(() => readToken());
  const [enabled, setEnabled] = useState<boolean>(() => readEnabled());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(PUSH_PREF_KEY, enabled ? '1' : '0');
    } catch {
      // localStorage 不可用（隱私模式）→ 偏好不持久化，不阻斷
    }
  }, [enabled]);

  const turnOff = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const t = readToken();
      if (t) {
        await deletePushToken(t);
        try {
          localStorage.removeItem(PUSH_TOKEN_KEY);
        } catch {
          /* ignore */
        }
        setToken(null);
      }
      setEnabled(false);
    } catch (e) {
      setError(e instanceof ApiError ? t('account.turnOffFailedMsg', { message: e.message }) : t('account.turnOffFailedRetry'));
    } finally {
      setBusy(false);
    }
  }, [t]);

  const turnOn = useCallback(() => {
    // 開啟 = 重新上報。Web v1 無瀏覽器 push 註冊 → 僅恢復偏好；手機 App 會在前景重新上報。
    setEnabled(true);
    setError(null);
  }, []);

  const on = enabled;

  return (
    <div className="card">
      <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.notifications')}</h2>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)' }}>
        <div style={{ minWidth: 0 }}>
          <div>{t('account.pushNotifications')}</div>
          <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
            {token
              ? t('account.pushDescWithToken')
              : t('account.pushDescNoToken')}
          </div>
        </div>
        <button
          role="switch"
          aria-checked={on}
          aria-label={t('account.pushNotifications')}
          disabled={busy}
          onClick={() => (on ? void turnOff() : turnOn())}
          style={{
            width: 44,
            height: 26,
            borderRadius: 'var(--radius-full)',
            border: '1px solid var(--color-surface-line)',
            background: on ? 'var(--color-brand-primary)' : 'var(--color-surface-panel2)',
            position: 'relative',
            flexShrink: 0,
            opacity: busy ? 'var(--opacity-disabled)' : 1,
          }}
        >
          <span
            style={{
              position: 'absolute',
              top: 2,
              left: on ? 20 : 2,
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left var(--motion-duration-fast) var(--motion-easing-ease)',
            }}
          />
        </button>
      </div>
      {error && (
        <p className="error-text" role="alert" style={{ margin: 'var(--spacing-3) 0 0' }}>
          {error}
        </p>
      )}
    </div>
  );
}
